/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Injectable, inject } from '@angular/core'
import { ListHelperService, MyProfileListMode, MyProfilePhoneService, MfaService } from '@myprofile-ui/myprofile-phone-utils'
import { PidxErrorService } from '@myprofile-ui/pidx-utils'
import { TranslateService } from '@ngx-translate/core'
import { Action, Selector, State, StateContext, Store } from '@ngxs/store'
import cloneDeep from 'lodash-es/cloneDeep'
import { combineLatest, of, throwError } from 'rxjs'
import { catchError, mergeMap, tap } from 'rxjs/operators'
import { MyProfileWidgetMode } from '../enums/myprofile-widget-mode'
import { CreatePhone, DeletePhone, SetEditorLoadingStatus, ShowBlankEditor, UpdatePhone } from './editor.actions'
import {
  GetPhones,
  GetProfile,
  SelectPhone,
  SetListOverlayLoadingStatus,
  SetPhoneBeingEdited,
  SetPhoneBeingVerified,
  SetPhoneListDisabled,
} from './list.actions'
import {
  GetMfaInformation,
  GetTranslations,
  SetAppErrorMessage,
  SetAppLoadingStatus,
  SetEditorErrorMessage,
  SetInitialConfiguration,
} from './phone-widget.actions'
import { ProfileService } from '../../../../../apps/myprofile-ui/src/app/services/profile/profile.service'
import { PhoneCommunicationDto, PhoneService } from '../../../../../apps/myprofile-ui/src/app/services/phone/phone.service'
import { HttpErrorResponse } from '@angular/common/http'

export interface PhoneWidgetStateModel {
  isCnUser: boolean
  mfaSmsEnrolled: boolean
  loading: boolean
  locale: string
  country: string
  errorMessage: string | null
  mode: MyProfileWidgetMode
  phones: PhoneCommunicationDto[]

  token: string

  editor: {
    loading: boolean
    visible: boolean
    tmpPhone: PhoneCommunicationDto | null | undefined
    errorMessage: string | null
  }

  phoneList: {
    disabled: boolean
    loading: boolean
    showDeleteButtons: boolean
    showEditButtons: boolean
    selectedPhoneId: string | null
    maxPhones: number
  }

  phoneIdBeingEdited: string | null
  phoneNumberBeingVerified: {
    number: string | null
    section: MyProfileListMode | null
    preferred: boolean
    phoneType?: string | null
  }
}

@State<PhoneWidgetStateModel>({
  name: 'phoneWidget',
  defaults: {
    isCnUser: false,
    mfaSmsEnrolled: false,
    loading: true,
    country: 'de',
    locale: 'de_DE',
    errorMessage: '',

    mode: MyProfileWidgetMode.PROFILE,
    phones: [],

    token: '',

    editor: {
      loading: false,
      visible: false,
      tmpPhone: null,
      errorMessage: null,
    },

    phoneList: {
      disabled: false,
      loading: false,
      showDeleteButtons: true,
      showEditButtons: true,
      selectedPhoneId: null,
      maxPhones: 5,
    },
    phoneIdBeingEdited: null,
    phoneNumberBeingVerified: {
      number: null,
      section: null,
      preferred: false,
      phoneType: null,
    },
  },
})
@Injectable()
export class PhoneWidgetState {
  private myProfilePhoneService = inject(MyProfilePhoneService)
  private phoneService = inject(PhoneService)
  private profileService = inject(ProfileService)
  private listHelperService = inject(ListHelperService)
  private pidxErrorService = inject(PidxErrorService)
  private _translateService = inject(TranslateService)
  private mfaService = inject(MfaService)
  private store = inject(Store)

  static getStandardPhone(phones: PhoneCommunicationDto[]): PhoneCommunicationDto | undefined {
    return phones.find((phone) => phone.isStandard)
  }

  @Selector()
  static isBlankCheckoutModeEditor(state: PhoneWidgetStateModel) {
    return !state.phones.length && state.mode !== MyProfileWidgetMode.PROFILE
  }

  @Action(SetAppLoadingStatus)
  setAppIsLoading({ patchState }: StateContext<PhoneWidgetStateModel>, { loading }: SetAppLoadingStatus) {
    patchState({ loading })
  }

  @Action(SetListOverlayLoadingStatus)
  setListOverlayLoadingStatus({ patchState, getState }: StateContext<PhoneWidgetStateModel>, { loading }: SetListOverlayLoadingStatus) {
    patchState({
      phoneList: {
        ...getState().phoneList,
        loading,
      },
    })
  }

  @Action(SetInitialConfiguration)
  SetInitialConfiguration(
    { patchState, getState }: StateContext<PhoneWidgetStateModel>,
    { mode, country, locale, token }: SetInitialConfiguration,
  ) {
    patchState({
      country,
      locale,
      mode,
      token,
    })
  }

  @Action(GetMfaInformation)
  getMfaInformation({ patchState }: StateContext<PhoneWidgetStateModel>) {
    return this.mfaService.getMfaStatus().pipe(
      tap((mfaStatus) => {
        patchState({ mfaSmsEnrolled: mfaStatus.mfaSmsEnrolled })
      }),
      catchError(() => {
        patchState({ mfaSmsEnrolled: false })
        return of(null)
      }),
    )
  }

  @Action(GetTranslations)
  getTranslations({ getState }: StateContext<PhoneWidgetStateModel>) {
    const { mode, locale, country } = getState()

    return this.myProfilePhoneService.getTranslations(country, locale).pipe(
      tap(async ({ translations }) => {
        if (mode !== MyProfileWidgetMode.PROFILE) {
          const lang = locale.split('_')[0]
          this._translateService.setFallbackLang(lang)
          this._translateService.setTranslation(lang, translations, true)
        }
      }),
    )
  }

  @Action(SetPhoneListDisabled)
  setPhoneListDisabled({ patchState, getState }: StateContext<PhoneWidgetStateModel>, { disabled }: SetPhoneListDisabled) {
    patchState({
      phoneList: {
        ...getState().phoneList,
        disabled,
      },
    })
  }

  @Action(SetPhoneBeingEdited)
  setPhoneBeingEdited({ patchState, getState }: StateContext<PhoneWidgetStateModel>, { phoneIdBeingEdited }: SetPhoneBeingEdited) {
    const state = getState()
    const tmpPhone = cloneDeep(state.phones.find((phone) => phone.id === phoneIdBeingEdited))

    patchState({
      errorMessage: null,
      phoneIdBeingEdited,
      editor: {
        ...state.editor,
        tmpPhone,
        visible: true,
      },
      phoneList: {
        ...state.phoneList,
        disabled: true,
      },
    })
  }

  @Action(SetPhoneBeingVerified)
  setPhoneBeingVerified({ patchState }: StateContext<PhoneWidgetStateModel>, { phoneNumberBeingVerified }: SetPhoneBeingVerified) {
    return patchState({
      phoneNumberBeingVerified: phoneNumberBeingVerified ?? { number: null, section: null, preferred: false, phoneType: null },
    })
  }

  @Action(GetPhones)
  getPhones({ patchState, getState }: StateContext<PhoneWidgetStateModel>) {
    const { country, locale } = getState()
    return this.phoneService.getPhoneList(country, locale).pipe(
      tap((phones) => {
        // default Phone Number will be selected, Mobile is preferred here
        phones = this.listHelperService.sortPhones(phones)

        // If there is only one phone number it should be standard
        if (phones.length === 1) {
          phones[0].isStandard = true
        }

        patchState({
          phones,
        })
      }),
      catchError((error) => {
        this.defaultErrorHandling(error)
        return throwError(() => error)
      }),
    )
  }

  @Action(GetProfile)
  getProfile({ patchState, getState }: StateContext<PhoneWidgetStateModel>) {
    const { country, locale } = getState()
    return this.profileService.getProfile(country, locale).pipe(
      tap(({ preferredCountry }) => {
        patchState({
          isCnUser: ['CN'].includes(preferredCountry ?? ''),
        })
      }),
      catchError((error) => {
        this.defaultErrorHandling(error)
        return throwError(() => error)
      }),
    )
  }

  // This only marks the entry in the list
  @Action(SelectPhone)
  selectPhone({ patchState, getState }: StateContext<PhoneWidgetStateModel>, { selectedPhoneId }: SelectPhone) {
    patchState({
      phoneList: {
        ...getState().phoneList,
        selectedPhoneId,
      },
    })
  }

  @Action(CreatePhone)
  createPhone({ getState }: StateContext<PhoneWidgetStateModel>, { phone }: CreatePhone) {
    const { country, locale, phones } = getState()
    if (phones.length === 0) {
      // this is the first phone that is added so it should be standard
      phone.isStandard = true
    }
    this.store.dispatch(new SetEditorLoadingStatus(true))

    return this.phoneService.addPhone(country, locale, phone).pipe(
      mergeMap((newPhone: PhoneCommunicationDto) => {
        return combineLatest([of(newPhone.id), this.store.dispatch(new GetPhones())])
      }),
      tap(([updatedPhoneId]) => {
        this.postCRUDPhoneCommands(updatedPhoneId)
      }),
      catchError((error) => {
        this.defaultErrorHandling(error, true)
        return throwError(() => error)
      }),
    )
  }

  @Action(UpdatePhone)
  updatePhone({ getState }: StateContext<PhoneWidgetStateModel>, { phone }: UpdatePhone) {
    const { country, locale } = getState()

    this.store.dispatch(new SetEditorLoadingStatus(true))
    return this.phoneService.updatePhone(country, locale, phone).pipe(
      mergeMap((updatedPhone: PhoneCommunicationDto) => {
        return combineLatest([of(updatedPhone.id), this.store.dispatch(new GetPhones())])
      }),
      tap(([updatedPhoneId]) => {
        this.postCRUDPhoneCommands(updatedPhoneId)
      }),
      catchError((error) => {
        this.defaultErrorHandling(error, true)
        return throwError(() => error)
      }),
    )
  }

  @Action(DeletePhone)
  deletePhone({ patchState, getState }: StateContext<PhoneWidgetStateModel>, { phone }: DeletePhone) {
    const { country, locale } = getState()

    this.store.dispatch(new SetListOverlayLoadingStatus(true))

    return this.phoneService.deletePhone(country, locale, phone).pipe(
      mergeMap(() => this.store.dispatch(new GetPhones())),
      tap(({ phoneWidget }) => {
        const standardPhone = PhoneWidgetState.getStandardPhone(phoneWidget.phones)
        if (getState().phones && getState().phones.length > 0) {
          this.postCRUDPhoneCommands(standardPhone?.id)
        } else {
          this.postCRUDPhoneCommands()
        }
      }),
      catchError((error) => {
        this.defaultErrorHandling(error)
        return throwError(() => error)
      }),
    )
  }

  @Action(SetEditorLoadingStatus)
  setEditorLoadingStatus({ patchState, getState }: StateContext<PhoneWidgetStateModel>, { loading }: SetEditorLoadingStatus) {
    patchState({
      editor: {
        ...getState().editor,
        loading,
      },
    })
  }

  // This is resetting statevalues used for editing
  @Action(ShowBlankEditor)
  showBlankEditor({ patchState, getState }: StateContext<PhoneWidgetStateModel>, { visible }: ShowBlankEditor) {
    const { editor, phoneList } = getState()
    patchState({
      editor: {
        ...editor,
        visible,
        tmpPhone: null,
      },
      phoneList: {
        ...phoneList,
        disabled: visible,
      },
      phoneIdBeingEdited: null,
    })
  }

  @Action(SetAppErrorMessage)
  setAppErrorMessage({ patchState }: StateContext<PhoneWidgetStateModel>, { errorMessage }: SetAppErrorMessage) {
    patchState({ errorMessage })
  }

  @Action(SetEditorErrorMessage)
  setEditorErrorMessage({ patchState, getState }: StateContext<PhoneWidgetStateModel>, { errorMessage }: SetEditorErrorMessage) {
    patchState({
      errorMessage: null,
      editor: {
        ...getState().editor,
        errorMessage,
      },
    })
  }

  postCRUDPhoneCommands(id?: string) {
    this.store.dispatch(new ShowBlankEditor(false))

    if (id) {
      this.store.dispatch(new SelectPhone(id))
    }

    this.store.dispatch(new SetAppErrorMessage(null))
    this.store.dispatch(new SetEditorErrorMessage(null))

    this.store.dispatch(new SetEditorLoadingStatus(false))
    this.store.dispatch(new SetListOverlayLoadingStatus(false))
  }

  private defaultErrorHandling(error: unknown, isEditor = false) {
    const errorKey = this.pidxErrorService.getErrorKeyForHttpErrorResponseByCode(
      error as HttpErrorResponse,
      'pidpaw.generic.error.backend.default',
    )

    if (isEditor) {
      this.store.dispatch(new SetEditorErrorMessage(errorKey))
    } else {
      this.store.dispatch(new SetAppErrorMessage(errorKey))
    }

    this.store.dispatch(new SetAppLoadingStatus(false))
    this.store.dispatch(new SetEditorLoadingStatus(false))
    this.store.dispatch(new SetListOverlayLoadingStatus(false))
  }
}
